import React, { FunctionComponent, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Paper,
  Switch,
  makeStyles,
  Avatar,
  Grid,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { 
  RealocacaoPedidosMpItens, 
  determinarResponsavel, 
  determinarStatusFaturamento, 
  StatusFaturamento,
  formatarData,
  formatarNumero 
} from '../types/RealocacaoPedidosMpItens.types';

const useStyles = makeStyles({
  accordion: {
    borderRadius: '16px !important',
    boxShadow: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    '&:before': {
      display: 'none',
    },
    overflow: 'hidden',
  },
  accordionSummary: {
    padding: '0 24px',
    minHeight: '40px',
    marginTop: '8px',
    '& .MuiAccordionSummary-content': {
      margin: '0',
    },
  },
  accordionDetails: {
    padding: '0 24px 24px 24px',
    flexDirection: 'column',
  },
  titleContainer: {
    paddingBottom: '12px',
    width: '100%',
  },
  title: {
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '4px',
    fontSize: '15px',
  },
  titleUnderline: {
    width: '100%',
    height: '2px',
    background: '#97877A',
    borderRadius: '4px',
    opacity: 0.5,
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#333',
    marginBottom: '12px',
    marginTop: '22px',
    fontSize: '14px',
    letterSpacing: '0.2px',
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:last-child)': {
      marginRight: '6px',
    },
  },
  sectionIcon: {
    fontSize: '16px',
    color: '#666',
  },

  headerPaper: {
    padding: '16px 20px',
    borderRadius: '10px',
    border: '1px solid #ebebeb',
    backgroundColor: '#fafafa',
    boxShadow: 'none',
  },
  label: {
    color: '#777',
    fontWeight: 600,
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    marginBottom: '3px',
    letterSpacing: '0.4px',
  },
  value: {
    fontWeight: 600,
    color: '#222',
    fontSize: '13px',
    lineHeight: 1.3,
  },
  valueSmall: {
    fontWeight: 500,
    color: '#555',
    fontSize: '12px',
    lineHeight: 1.3,
  },
  materialCard: {
    padding: '14px 18px',
    flex: 1,
    borderRadius: '10px',
    border: '1px solid #ebebeb',
    backgroundColor: '#fafafa',
    boxShadow: 'none',
  },
  labelGold: {
    color: '#b8960c',
    fontWeight: 700,
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    marginBottom: '3px',
    letterSpacing: '0.4px',
  },

  triangulacaoHeader: {
    padding: '8px 16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px 8px 0 0',
    border: '1px solid #eee',
    borderBottom: 'none',
    display: 'grid',
    gridTemplateColumns: '50px 1.3fr 1.3fr 2fr 90px',
    columnGap: '12px',
  },
  itemRow: {
    padding: '12px 16px',
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderTop: 'none',
    '&:last-child': {
      borderRadius: '0 0 8px 8px',
    },
    display: 'grid',
    gridTemplateColumns: '50px 1.3fr 1.3fr 2fr 90px',
    columnGap: '12px',
    alignItems: 'center',
    '& > *': {
      minWidth: 0,
    },
    '&:hover': {
      backgroundColor: '#fcfcfc',
    },
  },
  itemValue: {
    fontSize: '11.5px',
    color: '#2a2a2a',
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  itemValueSub: {
    fontSize: '10.5px',
    color: '#666',
    marginTop: '2px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
  },
  itemValueWrap: {
    fontSize: '11.5px',
    color: '#2a2a2a',
    fontWeight: 500,
    whiteSpace: 'normal',
    display: 'block',
    lineHeight: 1.2,
  },
  itemQuantity: {
    fontSize: '12px',
    color: '#222',
    fontWeight: 700,
    textAlign: 'right',
  },
  itemFlow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#444',
  },

  statusFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '24px',
    padding: '12px 20px',
    backgroundColor: '#fcfcfc',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
  },
  avatar: {
    width: '32px',
    height: '32px',
    fontSize: '14px',
    backgroundColor: '#444',
  }
});

interface DadosRealocacaoAccordionProps {
  data: RealocacaoPedidosMpItens;
  defaultExpanded?: boolean;
}

const DadosRealocacaoAccordion: FunctionComponent<DadosRealocacaoAccordionProps> = ({
  data,
  defaultExpanded = true,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const statusFaturamento = determinarStatusFaturamento(data.dataFaturamento);
  const responsavel = determinarResponsavel(data.dataFaturamento);
  const responsavelTexto = responsavel === 'COMPRADORA' ? 'Compradora' : 'Responsável PCP';
  const isFaturado = statusFaturamento === StatusFaturamento.FATURADO;

  return (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      elevation={0}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon style={{ fontSize: 20 }} />}
        className={classes.accordionSummary}
      >
        <Box className={classes.titleContainer}>
          <Typography className={classes.title}>Dados da Realocação</Typography>
          <Box className={classes.titleUnderline} />
        </Box>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        
        <Typography className={classes.sectionTitle}>
         Logística
        </Typography>
        <Paper className={classes.headerPaper} elevation={0}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography className={classes.label}>Rede</Typography>
              <Typography className={classes.value}>{data.descRedeLojas || '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Time</Typography>
              <Typography className={classes.value}>{data.time || '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Compradora</Typography>
              <Typography className={classes.value}>{data.compradora || '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Responsável PCP</Typography>
              <Typography className={classes.value}>{data.responsavelPcp || '—'}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Typography className={classes.sectionTitle}>
          Material
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Paper className={classes.materialCard} elevation={0}>
              <Typography className={classes.labelGold}>Código Material</Typography>
              <Typography className={classes.value} style={{ marginBottom: '2px' }}>{data.material}</Typography>
              <Typography className={classes.valueSmall}>{data.descMaterial}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.materialCard} elevation={0}>
              <Typography className={classes.label}>Cor</Typography>
              <Typography className={classes.value} style={{ marginBottom: '2px' }}>{data.corMaterial}</Typography>
              <Typography className={classes.valueSmall}>{data.descCorMaterial}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Typography className={classes.sectionTitle}>
         Pedido
        </Typography>
        <Paper className={classes.headerPaper} elevation={0}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography className={classes.label}>Fornecedor</Typography>
              <Typography className={classes.value}>{data.fornecedorMp}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Nº Pedido</Typography>
              <Typography className={classes.value}>{data.pedidoMp}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Emissão</Typography>
              <Typography className={classes.value}>{formatarData(data.dataEmissaoPedido)}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography className={classes.label}>Faturamento</Typography>
              <Typography className={classes.value}>{formatarData(data.dataFaturamento)}</Typography>
            </Grid>
          </Grid>
        </Paper>


        <Typography className={classes.sectionTitle}>
          Triangulações
        </Typography>
        
        <Box>
          <Box className={classes.triangulacaoHeader}>
            <Typography className={classes.label}>OP</Typography>
            <Box>
              <Typography className={classes.label} style={{ color: '#222', marginBottom: '2px', whiteSpace: 'nowrap' }}>Produto</Typography>
              <Typography style={{ fontSize: '9px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Programação</Typography>
            </Box>
            <Box>
              <Typography className={classes.label} style={{ color: '#222', marginBottom: '2px', whiteSpace: 'nowrap' }}>Pedido</Typography>
              <Typography style={{ fontSize: '9px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Material</Typography>
            </Box>
            <Typography className={classes.label}>Origem / Destino</Typography>
            <Box style={{ textAlign: 'right' }}>
              <Typography className={classes.label} style={{ color: '#222', marginBottom: '2px', whiteSpace: 'nowrap' }}>Qtd. Realocação</Typography>
              <Typography style={{ fontSize: '9px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Reserva Final</Typography>
            </Box>
          </Box>
          
          {data.itens.map((item, index) => (
            <Box key={item.id_item || index} className={classes.itemRow}>

              <Typography className={classes.itemValue}>{item.ordemProducao || '-'}</Typography>
              

              <Box style={{ minWidth: 0 }}>
                <Typography className={classes.itemValue} title={item.produto || '-'} style={{ fontWeight: 600 }}>{item.produto || '-'}</Typography>
                <Typography className={classes.itemValueSub} title={item.programacao || '-'}>{item.programacao || '-'}</Typography>
              </Box>


              <Box style={{ minWidth: 0 }}>
                <Typography className={classes.itemValue} title={item.pedidoMp || '-'} style={{ fontWeight: 600 }}>{item.pedidoMp || '-'}</Typography>
                <Typography className={classes.itemValueSub} title={item.material || '-'}>{item.material || '-'}</Typography>
              </Box>


              <Box style={{ minWidth: 0 }}>
                <Typography className={classes.itemValueWrap}>
                  {item.faccaoProducao || '-'}
                </Typography>
                {item.faccaoPedidoTriangulado && item.faccaoPedidoTriangulado !== '-' && (
                  <Box style={{ display: 'flex', alignItems: 'flex-start', marginTop: '2px' }}>
                    <ArrowForwardIcon style={{ fontSize: 12, color: '#999', marginRight: '4px', flexShrink: 0, marginTop: '1px' }} />
                    <Typography className={classes.itemValueWrap} style={{ color: '#555' }}>
                      {item.faccaoPedidoTriangulado}
                    </Typography>
                  </Box>
                )}
              </Box>


              <Box>
                <Typography className={classes.itemQuantity}>{formatarNumero(item.quantidadeRealocar)}</Typography>
                <Typography style={{ fontSize: '10px', color: '#666', textAlign: 'right', marginTop: '4px', lineHeight: 1.2 }}>
                  {formatarNumero(item.reservaFinal)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>


        <Box className={classes.statusFooter}>
          <Box display="flex" alignItems="center">
            <Avatar className={classes.avatar}>{responsavelTexto.charAt(0)}</Avatar>
            <Box ml={1.5}>
              <Typography className={classes.label} style={{ marginBottom: 0 }}>Responsável</Typography>
              <Typography className={classes.value} style={{ fontSize: '13px' }}>{responsavelTexto}</Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <Switch size="small" checked={isFaturado} disabled color="default" style={{ marginRight: '4px' }} />
            <Typography className={classes.value} style={{ fontSize: '11px', color: '#666' }}>
              {isFaturado ? 'FATURADO' : 'PENDENTE'}
            </Typography>
          </Box>
        </Box>

      </AccordionDetails>
    </Accordion>
  );
};

export default DadosRealocacaoAccordion;
